import { supabase } from "../supabaseClient"

const getTrimmedValue = (value) =>
  typeof value === "string" ? value.trim() : ""

const MIN_MATCH_LENGTH = 3

const normalizeKey = (value) =>
  getTrimmedValue(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")

const sanitizeOwner = (value) => {
  const trimmed = getTrimmedValue(value)
  if (!trimmed) return ""
  const normalized = trimmed.toLowerCase()
  if (normalized === "(not set)" || normalized === "not set") {
    return ""
  }
  return trimmed
}

const extractCanonicalName = (row) => {
  if (typeof row === "string") return getTrimmedValue(row)
  if (!row || typeof row !== "object") return ""
  const fromName = getTrimmedValue(row.name)
  if (fromName) return fromName
  const fromProductName = getTrimmedValue(row.product_name)
  if (fromProductName) return fromProductName
  return getTrimmedValue(row.product)
}

const buildCanonicalProducts = (rows) => {
  if (!Array.isArray(rows)) return []
  const entries = rows
    .map((row) => extractCanonicalName(row))
    .filter(Boolean)
    .map((name) => ({ key: normalizeKey(name), name }))
    .filter(({ key }) => Boolean(key))
    .sort((a, b) => b.key.length - a.key.length)

  const seen = new Set()
  const unique = []
  for (const entry of entries) {
    if (seen.has(entry.key)) continue
    seen.add(entry.key)
    unique.push(entry)
  }

  return unique
}

const findCanonicalProductName = (
  { owner, productName },
  canonicalProducts,
) => {
  const normalizedProduct = normalizeKey(productName)
  const normalizedOwner = normalizeKey(owner)

  if (normalizedProduct) {
    const exact = canonicalProducts.find(({ key }) => key === normalizedProduct)
    if (exact) return exact.name
  }

  if (normalizedProduct) {
    const partial = canonicalProducts.find(({ key }) =>
      normalizedProduct.includes(key),
    )
    if (partial) return partial.name
  }

  if (normalizedOwner) {
    const exactOwner = canonicalProducts.find(({ key }) => key === normalizedOwner)
    if (exactOwner) return exactOwner.name
  }

  if (normalizedOwner) {
    const partialOwner = canonicalProducts.find(({ key }) =>
      normalizedOwner.includes(key),
    )
    if (partialOwner) return partialOwner.name
  }

  return ""
}

const buildOwnerMappings = (rows) => {
  if (!Array.isArray(rows)) return []
  const entries = rows
    .map((row) => ({
      key: normalizeKey(row?.event_owner),
      productName: extractCanonicalName(
        row?.product_lookup ?? row?.productLookup ?? row?.product,
      ),
    }))
    .filter(({ key, productName }) => Boolean(key && productName))
    .sort((a, b) => b.key.length - a.key.length)

  const seen = new Set()
  const unique = []
  for (const entry of entries) {
    if (seen.has(entry.key)) continue
    seen.add(entry.key)
    unique.push(entry)
  }

  return unique
}

const findMappedProductName = (normalizedOwner, ownerMappings) => {
  if (!normalizedOwner) return ""
  for (const mapping of ownerMappings) {
    if (mapping.key === normalizedOwner) {
      return mapping.productName
    }

    if (
      mapping.key.length >= MIN_MATCH_LENGTH &&
      normalizedOwner.length >= MIN_MATCH_LENGTH
    ) {
      if (
        normalizedOwner.includes(mapping.key) ||
        mapping.key.includes(normalizedOwner)
      ) {
        return mapping.productName
      }
    }
  }

  return ""
}

const normalizeOwnerRow = (row, canonicalProducts, ownerMappings) => {
  if (!row || typeof row !== "object") {
    return { owner: "", productName: "" }
  }
  const owner = sanitizeOwner(row.eventOwner ?? row.event_owner ?? row.owner)
  const initialProduct = getTrimmedValue(
    row.productName ?? row.product_name ?? row.product,
  )

  const mappedProduct = findMappedProductName(normalizeKey(owner), ownerMappings)
  if (mappedProduct) {
    return { owner, productName: mappedProduct }
  }

  const productName = findCanonicalProductName(
    { owner, productName: initialProduct },
    canonicalProducts,
  )

  return { owner, productName }
}

const findExistingProductEntry = (entries, key) => {
  if (!key) return null

  let bestMatch = null
  for (const entry of entries) {
    if (entry.key === key) {
      return entry
    }

    if (
      entry.key.length >= MIN_MATCH_LENGTH &&
      key.length >= MIN_MATCH_LENGTH
    ) {
      if (entry.key.includes(key) || key.includes(entry.key)) {
        if (!bestMatch || entry.key.length > bestMatch.key.length) {
          bestMatch = entry
        }
      }
    }
  }

  return bestMatch
}

export async function applyEventOwnersToAccountProducts({
  accountId,
  organizationId,
  ownerRows,
}) {
  if (!accountId || !Array.isArray(ownerRows) || ownerRows.length === 0) {
    return
  }

  const [
    { data: existingProducts, error: existingError },
    ownerMappingResult,
  ] = await Promise.all([
    supabase
      .from("products")
      .select("id, product, event_owner")
      .eq("account_id", accountId),
    supabase
      .from("event_owner_product_mappings")
      .select("event_owner, product_lookup(name)")
      .order("event_owner", { ascending: true }),
  ])

  if (existingError) {
    console.error(
      "[Products] Failed to fetch products for event owner synchronization",
      {
        error: existingError,
        accountId,
      },
    )
    return
  }

  const { data: ownerMappingRows, error: ownerMappingError } =
    ownerMappingResult || {}
  if (ownerMappingError) {
    console.error(
      "[Products] Failed to load event owner mappings",
      {
        error: ownerMappingError,
        accountId,
      },
    )
  }

  let organizationProducts = []
  if (organizationId) {
    const { data: orgRows, error: orgError } = await supabase
      .from("products")
      .select("product")
      .eq("organization_id", organizationId)

    if (orgError) {
      console.error(
        "[Products] Failed to load organization products for event owner sync",
        {
          error: orgError,
          accountId,
          organizationId,
        },
      )
    } else {
      organizationProducts = orgRows || []
    }
  }

  const ownerMappings = buildOwnerMappings(ownerMappingRows)

  const canonicalProducts = buildCanonicalProducts([
    ...(organizationProducts || []),
    ...(existingProducts || []),
    ...ownerMappings.map((mapping) => mapping.productName),
  ])

  const assignments = new Map()
  for (const row of ownerRows) {
    const { owner, productName } = normalizeOwnerRow(
      row,
      canonicalProducts,
      ownerMappings,
    )
    const normalizedProductKey = normalizeKey(productName)
    if (!owner || !productName || !normalizedProductKey) continue
    if (!assignments.has(normalizedProductKey)) {
      assignments.set(normalizedProductKey, { owner, productName })
    }
  }

  if (assignments.size === 0) {
    return
  }

  const existingEntries = []
  for (const product of existingProducts || []) {
    if (typeof product?.product !== "string") continue
    const key = normalizeKey(product.product)
    if (!key) continue
    existingEntries.push({ key, product })
  }

  const updates = []
  for (const [key, { owner, productName }] of assignments.entries()) {
    const entry = findExistingProductEntry(existingEntries, key)
    const existing = entry?.product
    if (!existing) continue
    const currentOwner = getTrimmedValue(existing?.event_owner)
    const currentProduct = getTrimmedValue(existing?.product)
    const payload = {}

    if (owner && owner !== currentOwner) {
      payload.event_owner = owner
    }
    if (productName && productName !== currentProduct) {
      payload.product = productName
    }

    if (Object.keys(payload).length > 0) {
      updates.push({ id: existing.id, payload, productName })
    }
  }

  for (const update of updates) {
    const { error: updateError } = await supabase
      .from("products")
      .update(update.payload)
      .eq("id", update.id)
    if (updateError) {
      console.error(
        "[Products] Failed to update product from event owner sync",
        {
          error: updateError,
          accountId,
          product: update.productName,
        },
      )
    }
  }

  if (!organizationId) {
    return
  }

  const inserts = []
  for (const [key, { owner, productName }] of assignments.entries()) {
    if (findExistingProductEntry(existingEntries, key)) continue
    inserts.push({
      product: productName,
      event_owner: owner,
      account_id: accountId,
      organization_id: organizationId,
    })
  }

  if (!inserts.length) {
    return
  }

  const { error: insertError } = await supabase.from("products").insert(inserts)
  if (insertError) {
    console.error("[Products] Failed to insert products from event owners", {
      error: insertError,
      accountId,
      inserts: inserts.map((row) => ({
        product: row.product,
        event_owner: row.event_owner,
      })),
    })
  }
}
